import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { hasRealRoiVenta } from './RoiDisplay';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DESCRIPTION = 'Porcentaje aplicado sobre el precio de compra para estimar impuestos, notaría, registro, honorarios y otros gastos de adquisición. Puede ser sobrescrito activo a activo.';
const currency = value => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
const errorText = error => typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'No se pudo guardar. Inténtalo de nuevo.';

export const AcquisitionExpenses = ({ asset, assetId, readOnly = false, onAssetUpdated }) => {
  const globalMode = !assetId;
  const prefix = globalMode ? 'acquisition-settings' : 'asset-acquisition';
  const [defaultPct, setDefaultPct] = useState(null);
  const [value, setValue] = useState('');
  const [original, setOriginal] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [recalculationPending, setRecalculationPending] = useState(false);
  const pending = useRef(false);
  const saved = !globalMode && hasRealRoiVenta(asset);
  useEffect(() => {
    let active = true;
    setDefaultPct(null);
    axios.get(`${API}/settings/rental`, { withCredentials: true }).then(({ data }) => {
      if (!active) return;
      setDefaultPct(data.acquisition_expenses_percentage);
      setRecalculationPending(Boolean(data.recalculo_pendiente));
      if (globalMode) { setValue(String(data.acquisition_expenses_percentage)); setOriginal(String(data.acquisition_expenses_percentage)); }
    }).catch(() => { if (active) setError('No se pudo cargar el porcentaje global.'); });
    return () => { active = false; };
  }, [globalMode, assetId]);
  useEffect(() => {
    if (globalMode) return;
    const next = asset?.gastos_adquisicion_porcentaje == null ? '' : String(asset.gastos_adquisicion_porcentaje);
    setValue(next); setOriginal(next);
  }, [globalMode, asset?.gastos_adquisicion_porcentaje]);
  const persist = async next => {
    if (readOnly || pending.current || saved) return;
    const number = next === '' && !globalMode ? null : Number(next);
    if ((globalMode && next === '') || (number !== null && (!Number.isFinite(number) || number < 0 || number > 100))) {
      setError('Indica un porcentaje entre 0 y 100.'); return;
    }
    pending.current = true; setBusy(true); setError(''); setConfirmation('');
    try {
      if (globalMode) {
        const { data } = await axios.put(`${API}/settings/rental`, { acquisition_expenses_percentage: number }, { withCredentials: true });
        setDefaultPct(data.acquisition_expenses_percentage);
        setRecalculationPending(false);
        setConfirmation(`Parámetro guardado · ${data.activos_recalculados} activos recalculados`);
      } else {
        await axios.patch(`${API}/assets/${assetId}/acquisition-expenses`, { percentage: number }, { withCredentials: true });
        setConfirmation('Gastos estimados del activo guardados');
        onAssetUpdated?.();
      }
      setValue(next); setOriginal(next);
      toast.success('Gastos de adquisición guardados');
    } catch (err) { setError(errorText(err)); }
    finally { pending.current = false; setBusy(false); }
  };
  const price = Number(asset?.precio_compra || asset?.precio_compra_estimado || 0);
  const percentage = original === '' ? defaultPct : Number(original);
  const amount = saved ? (asset?.gastos_adquisicion_individualizados ?? ((asset?.gastos_totales || 0) + (asset?.itp_estimado || 0))) : price * (percentage || 0) / 100;
  return <section className="space-y-4 py-4 max-w-3xl" data-testid={`${prefix}-section`}>
    <h2 className="text-base md:text-lg font-semibold" data-testid={`${prefix}-title`}>Gastos de adquisición estimados</h2>
    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }} data-testid={`${prefix}-description`}>{DESCRIPTION}</p>
    {defaultPct === null && !error && <p className="text-sm" data-testid={`${prefix}-loading`}>Cargando porcentaje…</p>}
    {saved ? <p className="text-sm font-semibold" data-testid="asset-acquisition-individualized">Gastos individualizados: {currency(amount)}</p> : defaultPct !== null && <>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm space-y-1" htmlFor={`${prefix}-percentage`}>
          <span className="block">{globalMode ? 'Porcentaje por defecto' : 'Porcentaje del activo'}</span>
          <span className="flex items-center gap-2"><input id={`${prefix}-percentage`} data-testid={`${prefix}-percentage`} type="number" min="0" max="100" step="0.01"
            className="input-base w-32" placeholder={String(defaultPct)} value={value} disabled={busy || readOnly}
            onChange={event => { setValue(event.target.value); setError(''); setConfirmation(''); }} /><span>%</span></span>
        </label>
        {!readOnly && <>
          <button type="button" className="btn-primary flex items-center gap-2" disabled={busy || (value === original && !(globalMode && recalculationPending))} onClick={() => persist(value)} data-testid={`${prefix}-save`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Guardar
          </button>
          <button type="button" className="btn-secondary flex items-center gap-2" disabled={busy || (globalMode ? original === '15' : original === '')}
            onClick={() => { if (!globalMode || window.confirm('¿Restaurar el 15% y recalcular las estimaciones que dependen del valor global?')) persist(globalMode ? '15' : ''); }} data-testid={`${prefix}-reset`}>
            <RotateCcw className="w-4 h-4" />{globalMode ? 'Restaurar 15%' : 'Usar global'}
          </button>
        </>}
      </div>
      {!globalMode && <p className="text-sm" data-testid="asset-acquisition-estimate">{original === '' ? 'Global' : 'Personalizado'}: {percentage}% · {currency(amount)}</p>}
    </>}
    {error && <p role="alert" className="text-sm text-red-500" data-testid={`${prefix}-error`}>{error}</p>}
    {globalMode && recalculationPending && <p role="status" className="text-sm text-amber-600" data-testid="acquisition-settings-recalculation-pending">Recálculo pendiente. Guarda para completar la actualización de las estimaciones.</p>}
    {confirmation && <p role="status" className="text-sm text-emerald-600" data-testid={`${prefix}-confirmation`}>{confirmation}</p>}
  </section>;
};